// src/screens/CreateStoryScreen.js

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import './CreateStoryScreen.css';

import photoIcon from '../assets/photo-icon.png';
import backIcon from '../assets/backspace-icon.png';
import reloadIcon from '../assets/reload-icon.png';

import api from '../api';

import { compressImage } from '../utils/compressImage';
import { useDiscovery } from '../context/DiscoveryContext';

const STORY_MODE_IMAGE = 'image';
const STORY_MODE_TEXT = 'text';

const MAX_IMAGE_CAPTION_LENGTH = 220;
const MAX_TEXT_STORY_LENGTH = 250;

const DEFAULT_TEXT_BACKGROUND_KEY = 'mint';

const TEXT_STORY_BACKGROUNDS = [
  {
    key: 'coral',
    label: 'Coral',
    backgroundColor: '#ef746f',
    textColor: '#ffffff',
  },
  {
    key: 'mint',
    label: 'Mint',
    backgroundColor: '#74bea0',
    textColor: '#ffffff',
  },
  {
    key: 'blue',
    label: 'Blue',
    backgroundColor: '#4f83d6',
    textColor: '#ffffff',
  },
  {
    key: 'purple',
    label: 'Purple',
    backgroundColor: '#8064c4',
    textColor: '#ffffff',
  },
  {
    key: 'dark',
    label: 'Dark',
    backgroundColor: '#25282e',
    textColor: '#ffffff',
  },
];

function CreateStoryScreen() {
  const navigate = useNavigate();

  const {
    refetch: refetchDiscovery,
  } = useDiscovery();

  const [storyMode, setStoryMode] =
    useState(STORY_MODE_IMAGE);

  // Image story state.
  const [imageFile, setImageFile] =
    useState(null);

  const [previewUrl, setPreviewUrl] =
    useState('');

  const [caption, setCaption] =
    useState('');

  // Text story state.
  const [textStory, setTextStory] =
    useState('');

  const [
    backgroundKey,
    setBackgroundKey,
  ] = useState(
    DEFAULT_TEXT_BACKGROUND_KEY
  );

  const [submitting, setSubmitting] =
    useState(false);

  const [compressing, setCompressing] =
    useState(false);

  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: '',
  });

  const fileInputRef = useRef(null);
  const toastTimerRef = useRef(null);

  const selectedBackground =
    TEXT_STORY_BACKGROUNDS.find(
      (background) =>
        background.key === backgroundKey
    ) ||
    TEXT_STORY_BACKGROUNDS[1];

  const showToast = (
    message,
    type = 'success'
  ) => {
    if (toastTimerRef.current) {
      clearTimeout(
        toastTimerRef.current
      );
    }

    setToast({
      visible: true,
      message,
      type,
    });

    toastTimerRef.current =
      setTimeout(() => {
        setToast({
          visible: false,
          message: '',
          type: '',
        });
      }, 2000);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        try {
          URL.revokeObjectURL(
            previewUrl
          );
        } catch {}
      }

      if (toastTimerRef.current) {
        clearTimeout(
          toastTimerRef.current
        );
      }
    };
  }, [previewUrl]);

  const openPicker = (event) => {
    event?.stopPropagation?.();

    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleImageSelect = async (
    event
  ) => {
    const file =
      event.target.files?.[0];

    if (!file || compressing) {
      return;
    }

    if (
      file.size >
      50 * 1024 * 1024
    ) {
      showToast(
        'Image is too large to process on-device. Please choose a smaller one.',
        'error'
      );

      return;
    }

    try {
      setCompressing(true);

      let compressed =
        await compressImage(file);

      if (!('name' in compressed)) {
        const name =
          file.name || 'story.jpg';

        const type =
          compressed.type ||
          file.type ||
          'image/jpeg';

        compressed = new File(
          [compressed],
          name,
          {
            type,
          }
        );
      }

      if (previewUrl) {
        try {
          URL.revokeObjectURL(
            previewUrl
          );
        } catch {}
      }

      const nextPreviewUrl =
        URL.createObjectURL(
          compressed
        );

      setImageFile(compressed);
      setPreviewUrl(nextPreviewUrl);

      console.log(
        '📷 original:',
        Math.round(
          file.size / 1024
        ),
        'KB → compressed:',
        Math.round(
          compressed.size / 1024
        ),
        'KB',
        compressed.type
      );
    } catch (error) {
      console.error(
        '❌ Story image compression failed:',
        error
      );

      showToast(
        'Could not compress this image. Please try a different one.',
        'error'
      );
    } finally {
      setCompressing(false);
    }
  };

  const navigateToPublishedStory =
    async () => {
      try {
        await refetchDiscovery?.();
      } catch {}

      try {
        const currentUser =
          JSON.parse(
            localStorage.getItem(
              'barterfi_user'
            ) || 'null'
          );

        if (currentUser?.username) {
          window.dispatchEvent(
            new CustomEvent(
              'story:created',
              {
                detail: {
                  username:
                    currentUser.username,
                },
              }
            )
          );

          navigate(
            `/stories/${currentUser.username}`,
            {
              state: {
                from: 'publish',
              },
              replace: true,
            }
          );

          return;
        }

        navigate('/home');
      } catch {
        navigate('/home');
      }
    };

  const publishImageStory =
    async () => {
      if (!imageFile) {
        throw new Error(
          'Select an image first'
        );
      }

      const formData =
        new FormData();

      formData.append(
        'file',
        imageFile
      );

      const uploadResponse =
        await api.post(
          '/upload/story',
          formData,
          {
            headers: {
              'Content-Type':
                'multipart/form-data',
            },
          }
        );

      const imageUrl =
        uploadResponse?.data?.url;

      if (!imageUrl) {
        throw new Error(
          'No image URL returned from upload'
        );
      }

      await api.post('/stories', {
        contentType: 'image',
        imageUrl,

        text: caption.trim()
          ? caption.trim()
          : undefined,
      });
    };

  const publishTextStory =
    async () => {
      const cleanText =
        textStory.trim();

      if (!cleanText) {
        throw new Error(
          'Write something first'
        );
      }

      await api.post('/stories', {
        contentType: 'text',
        text: cleanText,
        backgroundKey,
      });
    };

  const handleSubmit = async () => {
    if (
      submitting ||
      compressing
    ) {
      return;
    }

    if (
      storyMode ===
        STORY_MODE_IMAGE &&
      !imageFile
    ) {
      return;
    }

    if (
      storyMode ===
        STORY_MODE_TEXT &&
      !textStory.trim()
    ) {
      return;
    }

    try {
      setSubmitting(true);

      if (
        storyMode ===
        STORY_MODE_TEXT
      ) {
        await publishTextStory();
      } else {
        await publishImageStory();
      }

      if (previewUrl) {
        try {
          URL.revokeObjectURL(
            previewUrl
          );
        } catch {}
      }

      showToast(
        storyMode === STORY_MODE_TEXT
          ? 'Text story published!'
          : 'Story uploaded!',
        'success'
      );

      await navigateToPublishedStory();
    } catch (error) {
      console.error(
        '❌ Failed to create story:',
        error
      );

      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message;

      showToast(
        serverMessage ||
          (
            storyMode ===
            STORY_MODE_TEXT
              ? 'Failed to publish text story.'
              : 'Failed to upload image story.'
          ),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const canPublish =
    storyMode === STORY_MODE_TEXT
      ? Boolean(
          textStory.trim()
        ) &&
        !submitting &&
        !compressing
      : Boolean(imageFile) &&
        !submitting &&
        !compressing;

  const publishButtonText =
    submitting
      ? 'Publishing…'
      : compressing
        ? 'Optimizing…'
        : 'Publish';

  return (
    <div className="create-story-screen">
      <div className="story-header">
        <img
          src={backIcon}
          alt="Back"
          className="createstory-back-icon"
          onClick={() =>
            navigate(-1)
          }
        />

        <h2>Create Story</h2>

        <button
          type="button"
          className="publish-btn"
          disabled={!canPublish}
          onClick={handleSubmit}
          title={
            compressing
              ? 'Compressing image…'
              : undefined
          }
        >
          {publishButtonText}
        </button>
      </div>

      <div className="story-mode-switch">
        <button
          type="button"
          className={`story-mode-btn ${
            storyMode ===
            STORY_MODE_IMAGE
              ? 'active'
              : ''
          }`}
          onClick={() =>
            setStoryMode(
              STORY_MODE_IMAGE
            )
          }
        >
          Photo
        </button>

        <button
          type="button"
          className={`story-mode-btn ${
            storyMode ===
            STORY_MODE_TEXT
              ? 'active'
              : ''
          }`}
          onClick={() =>
            setStoryMode(
              STORY_MODE_TEXT
            )
          }
        >
          Text
        </button>
      </div>

      <div className="story-content">
        {storyMode ===
        STORY_MODE_IMAGE ? (
          <>
            <div className="story-caption-bar">
              <textarea
                className="story-caption-input"
                placeholder="Add an optional caption…"
                value={caption}
                maxLength={
                  MAX_IMAGE_CAPTION_LENGTH
                }
                onChange={(event) =>
                  setCaption(
                    event.target.value
                  )
                }
              />

              <div className="story-caption-meta">
                <span className="count">
                  {caption.length}/
                  {
                    MAX_IMAGE_CAPTION_LENGTH
                  }
                </span>
              </div>
            </div>

            <div className="story-body">
              {!previewUrl ? (
                <div
                  className="image-upload-area"
                  onClick={openPicker}
                >
                  <img
                    src={photoIcon}
                    alt="Upload"
                    className="create-story-upload-icon"
                  />

                  <span>
                    Select a photo
                  </span>

                  <input
                    ref={fileInputRef}
                    className="image-file-input"
                    type="file"
                    accept="image/*"
                    onChange={
                      handleImageSelect
                    }
                  />
                </div>
              ) : (
                <div
                  className="image-upload-area image-selected"
                  onClick={openPicker}
                >
                  <img
                    src={previewUrl}
                    alt=""
                    className="story-backdrop"
                    aria-hidden="true"
                    draggable={false}
                  />

                  <img
                    src={previewUrl}
                    alt="Story preview"
                    className="story-media"
                    draggable={false}
                  />

                  {caption.trim() ? (
                    <div
                      className="story-overlay"
                      onClick={(event) =>
                        event.stopPropagation()
                      }
                    >
                      <div className="story-text">
                        {caption}
                      </div>
                    </div>
                  ) : null}

                  <input
                    ref={fileInputRef}
                    className="image-file-input"
                    type="file"
                    accept="image/*"
                    onChange={
                      handleImageSelect
                    }
                    onClick={(event) =>
                      event.stopPropagation()
                    }
                  />

                  <img
                    src={reloadIcon}
                    alt="Change"
                    className="change-image-icon"
                    onClick={(event) => {
                      event.stopPropagation();
                      openPicker(event);
                    }}
                    title="Change photo"
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-story-creator">
            <div
              className="text-story-preview"
              style={{
                backgroundColor:
                  selectedBackground.backgroundColor,

                color:
                  selectedBackground.textColor,
              }}
            >
              <textarea
                className="text-story-input"
                placeholder="Type your story…"
                value={textStory}
                maxLength={
                  MAX_TEXT_STORY_LENGTH
                }
                onChange={(event) =>
                  setTextStory(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="text-story-controls">
              <div className="text-story-count">
                {textStory.length}/
                {
                  MAX_TEXT_STORY_LENGTH
                }
              </div>

              <div
                className="text-story-colors"
                aria-label="Choose story background"
              >
                {TEXT_STORY_BACKGROUNDS.map(
                  (background) => (
                    <button
                      key={
                        background.key
                      }
                      type="button"
                      className={`text-story-color-btn ${
                        backgroundKey ===
                        background.key
                          ? 'active'
                          : ''
                      }`}
                      style={{
                        backgroundColor:
                          background.backgroundColor,
                      }}
                      onClick={() =>
                        setBackgroundKey(
                          background.key
                        )
                      }
                      aria-label={
                        background.label
                      }
                      title={
                        background.label
                      }
                    />
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {toast.visible && (
        <div
          className={`story-toast ${toast.type}`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default CreateStoryScreen;
